// src/utils/safeStorage.js

/**
 * Raw-string access to localStorage / sessionStorage that never throws.
 *
 * A browser can block storage (Safari with cookies blocked, some in-app
 * browsers). There, reading `window.localStorage` itself throws
 * `SecurityError: The operation is insecure.`, so an
 * `if (window.localStorage)` check does not protect the caller. Blocked
 * storage is treated as nothing stored and is not reported: it is the
 * visitor's setting, not a fault (#617).
 *
 * For JSON values in localStorage, use `utils/localStorageUtils`.
 */

/**
 * @param {'localStorage'|'sessionStorage'} name
 * @param {string} key
 * @returns {string|null} The stored string, or null if absent or blocked
 */
export const storageGet = (name, key) => {
  try {
    return window[name].getItem(key);
  } catch {
    return null;
  }
};

/**
 * @param {'localStorage'|'sessionStorage'} name
 * @param {string} key
 * @param {string} value
 * @returns {boolean} Whether the value was stored
 */
export const storageSet = (name, key, value) => {
  try {
    window[name].setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

/**
 * @param {'localStorage'|'sessionStorage'} name
 * @param {string} key
 * @returns {boolean} Whether the removal ran
 */
export const storageRemove = (name, key) => {
  try {
    window[name].removeItem(key);
    return true;
  } catch {
    return false;
  }
};

/**
 * Whether an error is the browser refusing storage access (Safari with
 * cookies blocked, some in-app browsers): a SecurityError, or Sentry's
 * reconstructed "SecurityError: …" message. That is the visitor's setting,
 * not a fault, so callers do not report it (#623). A full store or corrupt
 * data is something else and is still reported.
 * @param {Error} [error]
 * @returns {boolean}
 */
export const isStorageBlocked = (error) =>
  error?.name === 'SecurityError' ||
  /^SecurityError:|operation is insecure|Access is denied for this document/i.test(
    error?.message || '',
  );
